import React, { useEffect } from 'react';
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CheckCircle2,
  ChevronDown,
  Clock3,
  FileText,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Route,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import ContactRequestWizard from '../components/ContactRequestWizard';
import PageMeta from '../components/PageMeta';
import { useLanguage } from '../i18n/LanguageProvider';

const contactChannels = [
  {
    icon: MessageCircle,
    title: 'WhatsApp',
    subtitle: 'Échange rapide avec notre équipe',
    href: 'https://api.whatsapp.com/send/?phone=243892672472&text&type=phone_number&app_absent=0',
    external: true,
  },
  { icon: Phone, title: 'Téléphone', subtitle: '+243 892 672 472', href: 'tel:+243892672472' },
  { icon: Mail, title: 'Email', subtitle: 'info@wonyasoft.com', href: 'mailto:info@wonyasoft.com' },
];

const processSteps = [
  { icon: FileText, title: 'Réception du brief', text: 'Nous analysons votre demande et identifions le parcours adapté.' },
  { icon: Route, title: 'Qualification', text: 'Nous précisons le contexte, les volumes, les délais et les dépendances.' },
  { icon: Sparkles, title: 'Proposition', text: 'Vous recevez une prochaine étape claire : démo, estimation ou cadrage.' },
  { icon: CheckCircle2, title: 'Mise en œuvre', text: 'Nous lançons la configuration, l’intégration ou le projet validé.' },
];

const faqItems = [
  {
    question: 'Quel est votre délai moyen de réponse ?',
    answer: 'Nous répondons généralement sous 24 heures ouvrées. Une demande suffisamment détaillée peut être orientée plus rapidement.',
  },
  {
    question: 'Pouvez-vous nous aider à choisir entre API, BulkPay et sur mesure ?',
    answer: 'Oui. Le premier échange sert précisément à identifier la solution la plus adaptée à votre usage, vos volumes et votre organisation.',
  },
  {
    question: 'Le premier échange est-il payant ?',
    answer: 'Non. La qualification initiale et l’orientation sont gratuites. Un atelier approfondi peut ensuite être proposé si le projet l’exige.',
  },
  {
    question: 'Intervenez-vous uniquement en RD Congo ?',
    answer: 'Notre ancrage principal est en RD Congo, avec une présence à Kolwezi et Lubumbashi. Nous pouvons aussi accompagner des équipes à distance.',
  },
];

const Contact = () => {
  const { t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="ws-page ws-contact" data-no-i18n="true">
      <PageMeta
        title="Contact | WonyaSoft"
        description="Contactez WonyaSoft pour votre intégration API Mobile Money, vos paiements en masse, votre application ou votre solution monétique sur mesure."
      />

      <section className="ws-page-hero ws-page-hero--contact">
        <div className="ws-shell ws-page-hero__content">
          <span className="ws-eyebrow"><MessageCircle size={18} aria-hidden="true" /> {t('Contact projet et accompagnement')}</span>
          <h1>{t('Parlons du parcours de paiement adapté à votre activité')}</h1>
          <p>{t('Décrivez votre besoin en quelques lignes. Notre équipe vous aide à choisir entre API Mobile Money, BulkPay, application mobile ou développement sur mesure.')}</p>
          <div className="ws-button-row">
            <a className="ws-button ws-button--primary" href="#contact-form">{t('Démarrer le brief')} <ArrowRight size={18} aria-hidden="true" /></a>
            <a className="ws-button ws-button--secondary" href="https://api.whatsapp.com/send/?phone=243892672472&text&type=phone_number&app_absent=0" target="_blank" rel="noopener noreferrer">{t('WhatsApp direct')}</a>
          </div>
          <div className="ws-contact-hero__proofs" aria-label={t('Nos engagements de contact')}>
            <span><Clock3 size={17} aria-hidden="true" /> {t('Réponse sous 24 h ouvrées')}</span>
            <span><ShieldCheck size={17} aria-hidden="true" /> {t('Échanges confidentiels')}</span>
            <span><BadgeCheck size={17} aria-hidden="true" /> {t('Orientation personnalisée')}</span>
          </div>
        </div>
      </section>

      <section className="ws-section" aria-labelledby="ws-contact-form-title">
        <div className="ws-shell ws-contact-layout">
          <ContactRequestWizard />

          <div className="ws-contact-sidebar">
            <div className="ws-contact-sidebar__card">
              <span className="ws-kicker">{t('Canaux directs')}</span>
              <h2>{t('Choisissez le canal qui vous convient')}</h2>
              <div className="ws-contact-channel-list">
                {contactChannels.map(({ icon: Icon, title, subtitle, href, external }) => (
                  <a key={title} href={href} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined}>
                    <span><Icon size={21} aria-hidden="true" /></span>
                    <span><strong>{t(title)}</strong><small>{t(subtitle)}</small></span>
                    <ArrowRight size={17} aria-hidden="true" />
                  </a>
                ))}
              </div>
            </div>

            <div className="ws-contact-sidebar__card">
              <span className="ws-kicker">{t('Nos bureaux')}</span>
              <div className="ws-contact-office">
                <MapPin size={21} aria-hidden="true" />
                <div><strong>Kolwezi</strong><p>Avenue Mbaya Fara, commune de Manika, Lualaba</p></div>
              </div>
              <div className="ws-contact-office">
                <Building2 size={21} aria-hidden="true" />
                <div><strong>Lubumbashi</strong><p>6 Avenue Colonel Tshatshi, commune Ruashi</p></div>
              </div>
              <div className="ws-contact-hours">
                <Clock3 size={19} aria-hidden="true" />
                <span><strong>{t('Lundi – vendredi')}</strong><small>8h00 – 18h00</small></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ws-section ws-section--soft" aria-labelledby="ws-contact-process-title">
        <div className="ws-shell">
          <div className="ws-section-heading ws-section-heading--center">
            <span className="ws-kicker">{t('Prise en charge')}</span>
            <h2 id="ws-contact-process-title">{t('Une suite claire dès le premier échange')}</h2>
            <p>{t('Un parcours simple pour transformer votre demande en prochaine étape concrète.')}</p>
          </div>
          <ol className="ws-contact-process">
            {processSteps.map(({ icon: Icon, title, text }, index) => (
              <li key={title}>
                <span className="ws-contact-process__number">0{index + 1}</span>
                <Icon size={23} aria-hidden="true" />
                <h3>{t(title)}</h3>
                <p>{t(text)}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="ws-section" aria-labelledby="ws-contact-faq-title">
        <div className="ws-shell ws-contact-faq-layout">
          <div className="ws-section-heading">
            <span className="ws-kicker">FAQ</span>
            <h2 id="ws-contact-faq-title">{t('Avant de nous écrire')}</h2>
            <p>{t('Les réponses aux questions les plus courantes sur la prise de contact.')}</p>
          </div>
          <div className="ws-contact-faq">
            {faqItems.map(({ question, answer }) => (
              <details key={question}>
                <summary>{t(question)} <ChevronDown size={19} aria-hidden="true" /></summary>
                <p>{t(answer)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="ws-section">
        <div className="ws-shell ws-final-cta">
          <div>
            <span className="ws-kicker">WonyaSoft</span>
            <h2>{t('Vous souhaitez d’abord découvrir nos offres ?')}</h2>
            <p>{t('Comparez les solutions WonyaSoft avant de nous transmettre votre besoin.')}</p>
          </div>
          <Link className="ws-button ws-button--primary" to="/solutions">{t('Voir les solutions')} <ArrowRight size={18} aria-hidden="true" /></Link>
        </div>
      </section>
    </div>
  );
};

export default Contact;
